import { Button, Dropdown, MenuProps } from 'antd';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useMatch } from 'react-router-dom';
import { EventBusEventEnum } from '../../enums/EventBusEventEnum';
import { useFileUpload } from '../../hooks/useFileUpload';
import { usePermissions } from '../../hooks/usePermissions';
import EventBus from '../../services/EventBus';
import { Icon } from './Icon';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import RouterPageContext from '../../interfaces/RouterPageContext';

interface Props {
  context: RouterPageContext;
}

export const MainButtonMenu: React.FC<Props> = ({ context }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const { brandingData } = usePermissions();
  const { uploadFiles } = useFileUpload();

  const [isUploadDisabledInCurrentFolder, setIsUploadDisabledInCurrentFolder] =
    useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const matchFiles = useMatch('/files/*');

  useEffect(() => {
    const handler = (status: { isDisabled: boolean }) => {
      setIsUploadDisabledInCurrentFolder(status.isDisabled);
    };

    EventBus.on(EventBusEventEnum.IsUploadDisabledInCurrentFolder, handler);
    return () => {
      EventBus.off(EventBusEventEnum.IsUploadDisabledInCurrentFolder, handler);
    };
  }, []);

  /**
   * A memoized array of menu items for the dropdown menu.
   *
   * This array contains objects that define the properties of each menu item,
   * including label, icon, key, and type.
   *
   * @type {MenuProps['items']}
   */
  const dropdownItems: MenuProps['items'] = useMemo(() => {
    return [
      {
        label: t('common.createFolder'),
        icon: <Icon name="FolderIcon" className="h-5 w-5 text-yellow-dark" />,
        key: 'new-folder',
        onClick: () => {
          EventBus.emit(EventBusEventEnum.CreateFolder, {});
        },
      },
      {
        type: 'divider',
      },
      {
        label: t('common.uploadFile'),
        icon: <Icon name="FileUpIcon" className="h-5 w-5" />,
        key: 'upload-file',
        onClick: () => {
          fileInputRef.current?.click();
        },
      },
      {
        label: t('common.uploadFolder'),
        icon: <Icon name="FolderUpIcon" className="h-5 w-5" />,
        key: 'upload-folder',
        onClick: () => {
          folderInputRef.current?.click();
        },
      },
      {
        type: 'divider',
      },
      {
        label: t('common.wordDocument'),
        icon: <Icon name="WordFileIcon" className="h-5 w-5" />,
        key: 'create-word',
        onClick: () => {
          EventBus.emit(EventBusEventEnum.CreateFileFromTemplate, {
            template: 'word',
          });
        },
      },
      {
        label: t('common.excelDocument'),
        icon: <Icon name="ExcelFileIcon" className="h-5 w-5" />,
        key: 'create-excel',
        onClick: () => {
          EventBus.emit(EventBusEventEnum.CreateFileFromTemplate, {
            template: 'excel',
          });
        },
      },
      {
        label: t('common.pptDocument'),
        icon: <Icon name="PptFileIcon" className="h-5 w-5" />,
        key: 'create-powerpoint',
        onClick: () => {
          EventBus.emit(EventBusEventEnum.CreateFileFromTemplate, {
            template: 'powerpoint',
          });
        },
      },
    ];
  }, [t]);

  // const checkRedirectToFiles = () => {
  //   // Check if the dropzone is disabled wich means the file is not in the list and change the filters to go to myfiles
  //   if (
  //     props.isDropzoneDisabled &&
  //     (props.filters.list !== FileListFilterEnum.All ||
  //       props.filters.type !== 2 ||
  //       props.filters.table !== 0)
  //   ) {
  //     props.onFiltersChange({
  //       ...props.filters,
  //       count: PAG_COUNT,
  //       folder: null,
  //       list: FileListFilterEnum.All,
  //       offset: 0,
  //       orderAsc: DEFAULT_FILTERS.orderAsc,
  //       orderColumnFiles: DEFAULT_FILTERS.orderColumnFiles,
  //       table: 0,
  //       type: 2,
  //     });
  //   }
  // };

  /**
   * @desc
   * @param event
   * @return {Promise<void>}
   */
  const handleInputFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    //checkRedirectToFiles();
    uploadFiles(files as FileList);
    event.target.value = '';
  };

  /**
   * @desc
   * @param event
   * @return {Promise<void>}
   */
  const handleInputFolderChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    //checkRedirectToFiles();
    uploadFiles(files as FileList);
    event.target.value = '';
  };

  return (
    <div className="mx-auto mb-2 h-10 px-2 md:px-6 lg:w-[90%]">
      <input
        type="file"
        multiple
        ref={fileInputRef}
        className="hidden"
        onChange={handleInputFileChange}
      />
      <input
        type="file"
        multiple
        webkitdirectory="true"
        mozdirectory="true"
        ref={folderInputRef}
        className="hidden"
        onChange={handleInputFolderChange}
      />

      {!isUploadDisabledInCurrentFolder && matchFiles && (
        <Dropdown
          menu={{
            items: dropdownItems,
          }}
          className="w-full"
          trigger={['click']}
        >
          <Button
            type="primary"
            shape="round"
            className="w-full px-2 lg:ml-0 lg:px-4"
            style={{
              backgroundColor: brandingData?.mainButtonsColorBackground,
              color: brandingData?.mainButtonsColorForeground,
            }}
          >
            <div className="flex w-full flex-row items-center justify-center gap-1">
              <Icon
                name="FilePlusIcon"
                className="h-5 w-5"
                style={{
                  color: brandingData?.mainButtonsColorForeground,
                }}
              />
              <span
                className="hidden font-medium lg:block"
                style={{
                  color: brandingData?.mainButtonsColorForeground,
                }}
              >
                {t('common.new')}
              </span>
            </div>
          </Button>
        </Dropdown>
      )}

      {((): ReactNode => {
        switch (location.pathname) {
          case '/users':
            return (
              [UserRoleEnum.Admin].includes(
                context.authenticatedUser?.role || UserRoleEnum.Reader
              ) && (
                <Button
                  className="w-full"
                  type="primary"
                  shape="round"
                  onClick={() => {
                    EventBus.emit(EventBusEventEnum.CreateUser, {});
                  }}
                >
                  <div className="flex flex-row items-center justify-center gap-1">
                    <Icon name="UserPlusIcon" className="h-6" />
                    <span className="hidden font-medium lg:block">
                      {t('common.newUser')}
                    </span>
                  </div>
                </Button>
              )
            );
          case '/groups':
            return (
              [UserRoleEnum.Admin].includes(
                context.authenticatedUser?.role || UserRoleEnum.Reader
              ) && (
                <Button
                  className="w-full"
                  type="primary"
                  shape="round"
                  onClick={() => {
                    EventBus.emit(EventBusEventEnum.CreateGroup, {});
                  }}
                >
                  <div className="flex flex-row items-center justify-center gap-1">
                    <Icon name="UserPlusIcon" className="h-6" />
                    <span className="hidden font-medium lg:block">
                      {t('common.newGroup')}
                    </span>
                  </div>
                </Button>
              )
            );
          default:
            return <div className="h-[32px] w-full" />;
        }
      })()}
    </div>
  );
};

export default MainButtonMenu;
